import { selectMetadataTypesList } from '../../../reducers/documentation/metadataReducer';
import { useSelector } from 'react-redux';
import { useMetadata } from './useMetadata';
import { useEffect, useMemo, useState, useCallback, useRef, startTransition } from 'react';
import { MetadataSkeleton } from './MetadataSkeleton';
import { BaseLayout } from '../building-blocks/content/BaseLayout';
import { SubPageTypes } from '../types';
import { getMetadataLabel } from '../tabs/getMetadataLabel';
import { useDependencies } from '../dependencies/hooks/useDependencies';
import { SortOrder } from '../../common/types';
import { LayoutTableType } from '../building-blocks/content/types';
import { DownloadMetadata } from './DownloadMetadata';
import { DependenciesContentViewType } from '../dependencies/types';
import { DataTableId } from '../../common/table/utils';
import { Stack } from '@mui/material';
import { useMetadataList } from './useMetadataList';
import { MetadataFilters } from './MetadataFilters';
import { useRunOnce } from '../../common/useRunOnce';
import { useMetadataHandleDeepLink } from './useMetadataHandleDeepLinks';
import { useDocumentationFiltersContext } from '../selected-object/filters/DocumentationFiltersContext';
import { renderRows } from '../selected-object/expanded-mode/tableRows';
import { SalesforceItem } from '../../parser/ConfigurationItemTypes';
import { selectCrmOrgHasManagePackage } from '../../pages/environments/environmentsReducer';

const CLICK_THROTTLE_MS = 300; // Prevent clicks on same row within 300ms

interface MetadataContentProps {
  crmOrgId: string;
  activeTab: string;
  isExpandedMode: boolean;
}

export const MetadataContent = ({
  crmOrgId,
  activeTab,
  isExpandedMode,
}: MetadataContentProps) => {
  const isManagePackageInstalled = !!useSelector(selectCrmOrgHasManagePackage(crmOrgId));
  const metadataTypesList = useSelector(selectMetadataTypesList(crmOrgId));
  const metadataData = useMemo(
    () => metadataTypesList?.[activeTab],
    [metadataTypesList, activeTab],
  );

  const { items, onSortChange, _sortOptions, _sortKey, sortOrder, metadataColumns } =
    useMetadataList(crmOrgId, activeTab);
  const { clearAllFilterValues } = useDocumentationFiltersContext();

  const { fetchMetadata, setMetadataElement, clearMetadataElement } = useMetadata();
  const [isLoading, setIsLoading] = useState(false);
  const isFetchingRef = useRef(false); // Track fetching state to prevent duplicate requests

  // Performance optimization: Optimistic UI state for immediate click feedback
  const [clickedRowId, setClickedRowId] = useState<string | null>(null);
  const [isProcessingClick, setIsProcessingClick] = useState(false);

  // Throttling mechanism to prevent rapid successive clicks on same row
  const lastClickRef = useRef<{ rowId: string; timestamp: number } | null>(null);

  const label = getMetadataLabel(activeTab);
  const { handleSetConfigurationItem, handleCloseDependencies, activeItemId } =
    useDependencies(crmOrgId);

  const onRowClick = useCallback(
    (event: React.MouseEvent<HTMLTableCellElement>, row: any) => {
      const currentTime = Date.now();
      const lastClick = lastClickRef.current;

      // Throttling: Prevent rapid clicks on the same row
      if (
        lastClick &&
        lastClick.rowId === row.id &&
        currentTime - lastClick.timestamp < CLICK_THROTTLE_MS
      ) {
        return; // Ignore rapid successive clicks on same row
      }

      lastClickRef.current = { rowId: row.id, timestamp: currentTime };

      setClickedRowId(row.id);
      setIsProcessingClick(true);

      startTransition(() => {
        try {
          // Heavy Redux operations in transition (non-blocking)
          handleSetConfigurationItem({
            id: row.id,
            name: row._name,
            dependencyType: row._type,
            parentType: row._parentType,
            contentType: DependenciesContentViewType.default,
            clearHistory: true,
          });
          setMetadataElement(row.id, activeTab, crmOrgId);
        } catch (error) {
          console.error('Error handling row click:', error);
        } finally {
          // Reset optimistic UI state after operation completes
          setIsProcessingClick(false);
          setClickedRowId(null);
        }
      });
    },
    [crmOrgId, handleSetConfigurationItem, setMetadataElement, activeTab],
  );

  useMetadataHandleDeepLink({ onRowClick, elements: items, crmOrgId });

  useEffect(() => {
    return () => {
      handleCloseDependencies();
      clearMetadataElement();
      isFetchingRef.current = false;
    };
  }, [handleCloseDependencies, clearMetadataElement]);

  useEffect(() => {
    isFetchingRef.current = false;
  }, [activeTab, crmOrgId]);

  useEffect(() => {
    if (!metadataData && !isFetchingRef.current) {
      isFetchingRef.current = true;
      setIsLoading(true);

      fetchMetadata(activeTab, crmOrgId)
        .then(() => {
          setIsLoading(false);
          isFetchingRef.current = false;
        })
        .catch(() => {
          setIsLoading(false);
          isFetchingRef.current = false;
        });
    }
  }, [activeTab, crmOrgId, fetchMetadata, metadataData]);

  useRunOnce(() => {
    if (!!activeTab) {
      //clear metadata element filters when tab changes
      clearAllFilterValues();
    }
  });

  // Memoize JSX components separately to prevent recreation on every render
  const downloadCsvComponent = useMemo(() => <DownloadMetadata />, []);

  const filtersComponent = useMemo(
    () => <MetadataFilters crmOrgId={crmOrgId} activeTab={activeTab} />,
    [crmOrgId, activeTab],
  );

  const transformRows = useCallback(
    (rows: any[]) =>
      renderRows({
        items: rows as SalesforceItem[],
        activeItemId,
        isManagePackageInstalled,
      }),
    [activeItemId, isManagePackageInstalled],
  );

  const tableProps = useMemo(() => {
    const tableId = (DataTableId.DocumentationExpandedOtherTable + activeTab) as DataTableId;

    return {
      tableId: tableId,
      onRowClick,
      onSortChange: (props: { sortBy: string; sortOrder: SortOrder }) => {
        onSortChange(props.sortBy, props.sortOrder);
      },
      tableType: LayoutTableType.virtualizedTableWithCustomColumns,
      clickedRowId,
      isProcessingClick,
      transformRows,
    };
  }, [onSortChange, onRowClick, activeTab, clickedRowId, isProcessingClick, transformRows]);

  const topBarProps = useMemo(() => {
    return {
      label: label ?? '',
      downloadCsvJsx: downloadCsvComponent,
      sortOptions: _sortOptions,
      sortProps: { sortBy: _sortKey, sortOrder: sortOrder },
      filtersJsx: filtersComponent,
      showResultsCount: true,
    };
  }, [downloadCsvComponent, filtersComponent, _sortOptions, _sortKey, sortOrder, label]);

  if (isLoading) {
    return <MetadataSkeleton />;
  }

  return (
    <Stack pt={isExpandedMode ? 3 : 0} height={'100%'}>
      <BaseLayout
        pageType={SubPageTypes.objects}
        columns={metadataColumns}
        rows={items}
        topBarProps={topBarProps}
        tableProps={tableProps}
        crmOrgId={crmOrgId}
        isExpandedMode={isExpandedMode}
      />
    </Stack>
  );
};
